/**
 * Car state and controller input — the shared contract every car system
 * (ground control, aerial control, jump/dodge, boost) reads and mutates.
 *
 * Kept separate from the systems themselves so the systems can be developed
 * and tested independently without importing each other.
 *
 * EVERYTHING A CAR SYSTEM REMEMBERS BETWEEN TICKS LIVES HERE, as plain
 * serializable data. Three systems used to keep their per-car memory in
 * module-level WeakMaps keyed by the CarState — the powerslide ramp (./ground),
 * the boost burst (./boost) and the jump button's previous level (./jump) —
 * because their authors did not own this file. That made a CarState neither
 * cloneable nor serializable: a structured clone, a worker transfer or a
 * snapshot/restore silently dropped three pieces of state and the copy diverged
 * from the original under exactly the inputs that exercise them. The fields are
 * now on the type and the WeakMaps are gone. The accessors in those modules
 * (`powerslideValue`, `boostBurst`, `wasJumpDown`) survive as thin readers.
 *
 * THE CAR'S BODY IS ON THE CAR, for exactly the same reason. `config` is the
 * hitbox, the wheels and the suspension rest lengths of whichever preset this
 * car is (../physics/car-config), and every system that used to import the
 * Octane's numbers from ./constants now reads them from here. Putting it on the
 * state rather than passing it as a parameter is what makes a Dominus survive a
 * clone, a serialize/deserialize round trip and a worker transfer: a car whose
 * body were a call-site argument would silently come back an Octane on the
 * other side of any of those, which is precisely the failure the WeakMaps had.
 * It is the one field on this type that is a shared frozen SINGLETON rather
 * than per-car data — see `cloneState` in src/sim/state.ts.
 */

import { OCTANE_CONFIG, type CarConfig } from './car-config';
import { identity3, type Mat3, type Vec3, vec3 } from './math';

/** Which surface the car is currently attached to, if any. */
export type Surface = 'none' | 'floor' | 'wall' | 'ceiling' | 'ramp';

export interface CarInput {
  /** -1 .. 1. Reverse/brake when it opposes the car's forward velocity. */
  throttle: number;
  /** -1 .. 1. Positive steers left (matching the +Y-is-left convention). */
  steer: number;
  /** -1 .. 1. Airborne nose up/down. */
  pitch: number;
  /** -1 .. 1. Airborne rotation about the up axis. */
  yaw: number;
  /** -1 .. 1. Airborne rotation about the forward axis. */
  roll: number;
  jump: boolean;
  boost: boolean;
  /** Powerslide on the ground; air roll modifier in the air. */
  handbrake: boolean;
}

export const neutralInput = (): CarInput => ({
  throttle: 0,
  steer: 0,
  pitch: 0,
  yaw: 0,
  roll: 0,
  jump: false,
  boost: false,
  handbrake: false,
});

/**
 * The boost burst — is a burst running, and for how long. Owned by ./boost;
 * declared here because it is per-car state and all per-car state is on
 * CarState. See BOOST_MIN_TIME: a burst outlives the button by 0.1 s, so the
 * button alone cannot reconstruct it.
 */
export interface BoostBurstState {
  /** True while the burst is producing thrust and consuming boost. */
  active: boolean;
  /** Seconds the current burst has run, for the BOOST_MIN_TIME rule. */
  time: number;
}

/**
 * Who this car last bumped or demolished, and how long it must wait before it
 * may do so again. Owned by ./car-car.
 *
 * ONE SLOT, ON THE ATTACKER, KEYED TO A VICTIM. This is RocketSim's
 * `CarState::carContact` verbatim and the shape is the behaviour: the cooldown
 * suppresses a second bump on `otherCarIndex` only, so a car that has just
 * bumped A can still bump B on the very next tick, and A can bump back
 * immediately because A has its own slot. A global or pair-symmetric cooldown
 * gets all three of those wrong.
 *
 * It is per-car STATE and lives here rather than in a module-level WeakMap for
 * the reason at the top of this file — see `boostBurst`.
 */
export interface CarContactState {
  /** Index into the world's car list, or -1 for "nobody". */
  otherCarIndex: number;
  /** Seconds left before this car may bump `otherCarIndex` again. */
  cooldownTimer: number;
}

export interface CarState {
  /**
   * Which car body this is: hitbox, wheels, suspension rest lengths and dodge
   * deadzone. Defaults to `OCTANE_CONFIG`, which is what every car in this tree
   * was before bodies existed. Frozen and shared — never mutate it, and swap
   * the whole object to change body.
   */
  config: CarConfig;

  position: Vec3;
  velocity: Vec3;
  angularVelocity: Vec3;
  /** Columns are (forward, left, up) in world space. */
  orientation: Mat3;

  /** Which surface the car is driving on. `none` means airborne. */
  surface: Surface;
  /** Contact normal of the surface the car is attached to. */
  surfaceNormal: Vec3;
  /** True while at least one wheel has ground contact. */
  onGround: boolean;

  /** 0 .. 100. */
  boostAmount: number;
  /** True while at or above the supersonic threshold (with hysteresis). */
  supersonic: boolean;
  /** Seconds remaining in the supersonic-maintain grace window. */
  supersonicTimer: number;

  // --- jump / dodge state machine ---
  /** True from the moment the jump input is pressed until the car lands. */
  hasJumped: boolean;
  /** Consumed by a double jump or a dodge; refreshed on landing. */
  hasFlip: boolean;
  /** True while a dodge/flip is in progress. */
  isDodging: boolean;
  /** Seconds the jump input has been held, for the sustained-force window. */
  jumpHoldTime: number;
  /** Seconds since the jump ended, gating the double jump / dodge window. */
  airTime: number;
  /** Seconds elapsed in the current dodge. */
  dodgeTime: number;
  /** Direction of the active dodge in the car's local frame. */
  dodgeDirection: Vec3;
  /** Seconds remaining before the car may dodge again after landing. */
  dodgeCooldown: number;
  /**
   * Previous tick's jump-button level. ./jump edge-detects the button
   * (`jump && !prevJumpDown`); without the edge, holding jump re-jumps the
   * instant the car lands and burns the double jump the instant the hold
   * window expires.
   */
  prevJumpDown: boolean;

  /** Analog powerslide value, 0..1 — the handbrake ramp owned by ./ground. */
  powerslide: number;

  /** Boost burst state, owned by ./boost. */
  boostBurst: BoostBurstState;
  /**
   * Seconds since this car last spent boost, for the RECHARGE boost mutator's
   * RECHARGE_BOOST_DELAY. Owned by ./boost, and advanced ONLY while that mutator
   * is active — under every other boost setting it stays at 0, so the default
   * game carries this field without ever writing to it.
   */
  boostRechargeTimer: number;

  /** Bump/demolition cooldown against one specific victim. Owned by ./car-car. */
  carContact: CarContactState;

  /** True when the car has been demolished and is awaiting respawn. */
  demolished: boolean;
  /** Seconds remaining until respawn. */
  respawnTimer: number;

  /** Team index; 0 = blue (defends -Y), 1 = orange (defends +Y). */
  team: 0 | 1;
}

export const makeCar = (team: 0 | 1 = 0, config: CarConfig = OCTANE_CONFIG): CarState => ({
  config,

  position: vec3(0, 0, 17),
  velocity: vec3(0, 0, 0),
  angularVelocity: vec3(0, 0, 0),
  orientation: identity3(),

  surface: 'floor',
  surfaceNormal: vec3(0, 0, 1),
  onGround: true,

  boostAmount: 33,
  supersonic: false,
  supersonicTimer: 0,

  hasJumped: false,
  hasFlip: true,
  isDodging: false,
  jumpHoldTime: 0,
  airTime: 0,
  dodgeTime: 0,
  dodgeDirection: vec3(0, 0, 0),
  dodgeCooldown: 0,
  prevJumpDown: false,

  powerslide: 0,
  boostBurst: { active: false, time: 0 },
  boostRechargeTimer: 0,

  carContact: { otherCarIndex: -1, cooldownTimer: 0 },

  demolished: false,
  respawnTimer: 0,

  team,
});
